import React from 'react';
import {
  TableRow,
  TableCell,
  Box,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  useTheme,
} from '@mui/material';
import {
  Visibility as ViewIcon,
  MoreVert as MoreVertIcon,
} from '@mui/icons-material';
import { formatDate, getStatusColor, getStatusIcon } from '../../utils/helpers';
import { getStatusChipStyle } from '../ExperimentDetailsDialogComponents/utils';
import { SPACING } from '../../../../theme';
import { adjustOpacity } from '../../../../theme/utils';

/**
 * ExperimentTableRow Component
 *
 * Individual experiment row in the table.
 *
 * @param {Object} props
 * @param {Object} props.experiment - Experiment object
 * @param {Object} props.templateNames - Map of template IDs to names
 * @param {Function} props.onExperimentClick - Handler for clicking experiment
 * @param {Function} props.onActionMenuOpen - Handler for opening action menu
 */
function ExperimentTableRow({
  experiment,
  templateNames,
  onExperimentClick,
  onActionMenuOpen,
}) {
  const theme = useTheme();

  return (
    <TableRow
      key={experiment.id}
      hover
      onClick={() => onExperimentClick(experiment)}
      sx={{
        cursor: 'pointer',
        '&:hover': {
          backgroundColor:
            theme.palette.action.hover || theme.palette.background.default,
        },
        '& .MuiTableCell-root': {
          borderBottom: `1px solid ${theme.palette.divider}`,
          padding: SPACING.md,
        },
      }}
    >
      <TableCell sx={{ maxWidth: '400px' }}>
        <Box>
          <Typography
            variant="subtitle2"
            fontWeight="medium"
            sx={{
              wordBreak: 'break-word',
              whiteSpace: 'normal',
            }}
          >
            {experiment.name}
          </Typography>
          {experiment.description && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                wordBreak: 'break-word',
                whiteSpace: 'normal',
              }}
            >
              {experiment.description}
            </Typography>
          )}
        </Box>
      </TableCell>
      <TableCell sx={{ minWidth: '300px' }}>
        <Typography
          variant="body2"
          sx={{
            fontFamily: 'monospace',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {templateNames[experiment.template_id] || 'Loading...'}
        </Typography>
      </TableCell>
      <TableCell>
        <Chip
          label={experiment.status}
          color={getStatusColor(experiment.status)}
          size="small"
          icon={getStatusIcon(experiment.status)}
          sx={{
            ...getStatusChipStyle(experiment.status, theme),
            width: 110,
            height: 24,
            padding: 0,
            boxSizing: 'border-box',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            '& .MuiChip-label': {
              paddingLeft: '6px', // Padding between icon and text
              paddingRight: '8px', // Right padding
              fontSize: '0.75rem',
              fontWeight: 500,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              display: 'flex',
              alignItems: 'center',
              lineHeight: 1,
            },
            '& .MuiChip-icon': {
              marginLeft: '6px', // Left padding
              marginRight: 0, // No right margin, padding handled by label
              width: 16,
              height: 16,
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            },
          }}
        />
      </TableCell>
      <TableCell>
        <Typography variant="body2">
          {experiment.treatments?.length || 0} treatments
        </Typography>
        {experiment.treatments && experiment.treatments.length > 0 && (
          <Typography variant="caption" color="text.secondary">
            Versions:{' '}
            {[
              ...new Set(experiment.treatments.map(t => t.template_version)),
            ].join(', ')}
          </Typography>
        )}
      </TableCell>
      <TableCell>
        <Typography variant="body2" color="text.secondary">
          {experiment.created_by || 'N/A'}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2" color="text.secondary">
          {experiment.updated_by || 'N/A'}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2">
          {formatDate(experiment.created_at)}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2">
          {formatDate(experiment.updated_at)}
        </Typography>
      </TableCell>
      <TableCell align="center">
        <Box display="flex" gap={1} justifyContent="center" alignItems="center">
          <Tooltip title="View Details">
            <IconButton
              size="small"
              onClick={e => {
                e.stopPropagation();
                onExperimentClick(experiment);
              }}
              sx={{
                color: theme.palette.primary.main,
                '&:hover': {
                  backgroundColor: adjustOpacity(
                    theme.palette.primary.main,
                    0.1
                  ),
                },
              }}
            >
              <ViewIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title="More Actions">
            <IconButton
              size="small"
              onClick={e => onActionMenuOpen(e, experiment)}
              sx={{
                color: theme.palette.text.secondary,
                '&:hover': {
                  backgroundColor: adjustOpacity(
                    theme.palette.text.secondary,
                    0.1
                  ),
                  color: theme.palette.text.secondary,
                },
              }}
            >
              <MoreVertIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </TableCell>
    </TableRow>
  );
}

export default ExperimentTableRow;
